import type {
  AnyElement,
  LogoElement,
  ShapeElement,
  TextElement,
} from '@/components/create/v2/sections/media-studio/text-editor/useTextEditorStore';
import { PER_VARIANT_PROPERTIES, PROPAGATING_PROPERTIES } from '@/lib/concept-workbench';
import { fontScaleFromSize } from '@/lib/concept-workbench/stage-dimensions';
import type {
  AspectRatioToken,
  LayerOverride,
  VariantLayerGeometry,
} from '@/lib/concept-workbench/types';
import type {
  OverlayLayer,
  OverlayLogoLayer,
  OverlayShapeLayer,
  OverlayTextLayer,
} from '@/lib/media-studio/overlay-types';

/**
 * THE BRIDGE between the variant-set document and the Konva engine.
 *
 * The engine (`KonvaTextNode` / `KonvaShapeNode` / `KonvaLogoNode` and their
 * store) is already built and lives under the PROTECTED Home-composer tree, so
 * it is reused verbatim and never forked. It speaks `AnyElement` in display-stage
 * pixels; the workbench speaks a set document plus per-ratio geometry. This file
 * is the only place the two vocabularies meet.
 *
 * The direction of travel matters:
 *
 *   set document ──materialiseVariant──▶ OverlayLayer[] ──here──▶ AnyElement[]
 *   AnyElement (after a drag) ──here──▶ { geometry, propagating } ──▶ store
 *
 * `diffElement` is the load-bearing half. A drag writes `x`/`y`, which must go to
 * THIS RATIO ONLY; a colour change writes `fill`, which must propagate to every
 * sibling. Getting that backwards is not a cosmetic bug — it either makes a
 * 1080×1920 headline inherit a 1080×1080 centre, or it silently un-brands three
 * of four sizes. So the routing is derived from the same two sets the store's own
 * `splitPatch` uses, never from a hand-typed list here.
 */

const DEFAULT_CONTRAST_PADDING = { top: 0, right: 0, bottom: 0, left: 0 };

function textElementFrom(layer: OverlayTextLayer): TextElement {
  return {
    id: layer.id,
    text: layer.text,
    x: layer.x,
    y: layer.y,
    width: layer.width,
    height: layer.height,
    fontSize: layer.fontSize,
    fontFamily: layer.fontFamily,
    fontWeight: layer.fontWeight,
    isItalic: layer.isItalic ?? false,
    isUnderline: layer.isUnderline ?? false,
    textAlign: layer.textAlign,
    verticalAlign: layer.verticalAlign,
    textTransform: layer.textTransform,
    lineHeight: layer.lineHeight,
    letterSpacing: layer.letterSpacing,
    fill: layer.fill,
    contrastMode: layer.contrastMode,
    contrastColor: layer.contrastColor,
    contrastIntensity: layer.contrastIntensity,
    contrastPadding: layer.contrastPadding ?? DEFAULT_CONTRAST_PADDING,
    boxRoundness: layer.boxRoundness ?? 0,
    boxShapeType: layer.boxShapeType,
    boxFeather: layer.boxFeather,
    scaleX: layer.scaleX ?? 1,
    scaleY: layer.scaleY ?? 1,
    rotation: layer.rotation,
    zIndex: layer.zIndex,
  };
}

function logoElementFrom(layer: OverlayLogoLayer): LogoElement {
  return {
    id: layer.id,
    kind: 'logo',
    logoUrl: layer.logoUrl,
    logoVariant: layer.logoVariant,
    x: layer.x,
    y: layer.y,
    width: layer.width,
    height: layer.height,
    rotation: layer.rotation,
    scaleX: layer.scaleX,
    scaleY: layer.scaleY,
    opacity: layer.opacity,
    zIndex: layer.zIndex,
  };
}

function shapeElementFrom(layer: OverlayShapeLayer): ShapeElement {
  return {
    id: layer.id,
    kind: 'shape',
    shapeType: layer.shapeType,
    x: layer.x,
    y: layer.y,
    width: layer.width,
    height: layer.height,
    rotation: layer.rotation,
    fill: layer.fill,
    opacity: layer.opacity,
    feather: layer.feather,
    cornerRadius: layer.cornerRadius,
    points: layer.points,
    innerRadius: layer.innerRadius,
    strokeWidth: layer.strokeWidth,
    strokeColor: layer.strokeColor,
    zIndex: layer.zIndex ?? 0,
  };
}

export function overlayLayerToElement(layer: OverlayLayer): AnyElement {
  if (layer.kind === 'logo') return logoElementFrom(layer);
  if (layer.kind === 'shape') return shapeElementFrom(layer);
  return textElementFrom(layer);
}

/** The render list for one variant, in the shape the Konva nodes consume. */
export function overlayLayersToElements(layers: readonly OverlayLayer[]): AnyElement[] {
  return layers.map(overlayLayerToElement);
}

export function geometryFromElement(element: AnyElement): VariantLayerGeometry {
  const withScale = element as { scaleX?: number; scaleY?: number; height?: number };
  return {
    x: element.x,
    y: element.y,
    width: element.width,
    height: withScale.height,
    rotation: element.rotation ?? 0,
    scaleX: withScale.scaleX ?? 1,
    scaleY: withScale.scaleY ?? 1,
  };
}

export interface ElementDiff {
  /** Per-ratio. Goes to `updateGeometry` and never marks a sibling. */
  geometry: Partial<VariantLayerGeometry>;
  /** Set-level. Goes to `updateSetLayer` (or `overrideVariantLayer`) and propagates. */
  propagating: LayerOverride;
}

export function isEmptyDiff(diff: ElementDiff): boolean {
  return Object.keys(diff.geometry).length === 0 && Object.keys(diff.propagating).length === 0;
}

function changed(a: unknown, b: unknown): boolean {
  if (a === b) return false;
  if (typeof a === 'object' && typeof b === 'object' && a !== null && b !== null) {
    return JSON.stringify(a) !== JSON.stringify(b);
  }
  return true;
}

/**
 * Split what the engine just changed into "this ratio" and "the whole set".
 *
 * `fontSize` is the one property that translates rather than routes. The engine
 * writes pixels (a corner-drag scales the font); the set stores the dimensionless
 * `fontScale`. Writing the pixel value onto the set would make a 1920-wide 16:9
 * inherit a 1080-wide headline size verbatim — the exact auto-resize failure four
 * competitors shipped and then walked back.
 */
export function diffElement(
  previous: AnyElement,
  next: AnyElement,
  ratio: AspectRatioToken,
): ElementDiff {
  const geometry: Record<string, unknown> = {};
  const propagating: Record<string, unknown> = {};

  const before = previous as unknown as Record<string, unknown>;
  const after = next as unknown as Record<string, unknown>;

  for (const key of Object.keys(after)) {
    if (key === 'id' || key === 'kind') continue;
    if (!changed(before[key], after[key])) continue;

    if (key === 'fontSize') {
      propagating.fontScale = fontScaleFromSize(after[key] as number, ratio);
      continue;
    }
    if (PER_VARIANT_PROPERTIES.has(key)) {
      geometry[key] = after[key];
      continue;
    }
    if (PROPAGATING_PROPERTIES.has(key)) {
      propagating[key] = after[key];
    }
    // Anything in neither set is deliberately dropped rather than guessed at.
  }

  return {
    geometry: geometry as Partial<VariantLayerGeometry>,
    propagating: propagating as LayerOverride,
  };
}

/** Every element the engine holds, keyed for an O(1) diff against the next tick. */
export function indexElements(elements: readonly AnyElement[]): Map<string, AnyElement> {
  return new Map(elements.map((element) => [element.id, element]));
}
